import { useState } from 'react'
import { ItemLine } from './ItemLine'
import { Icone } from '@/components/ui/Icone'
import type { Carte, Devoilement, ReponseComposee } from '@/lib/api/feed'

/**
 * La relecture d'une épreuve — RG-3.11quinquies, [DECISIONS §38].
 *
 * **Les deux moitiés de la règle vont ensemble.** Cacher le verdict pendant
 * l'épreuve est acquis (RG-3.11quater) : la correction immédiate transforme la
 * mesure en série d'indices. Mais s'arrêter là laissait le candidat avec un
 * pourcentage et rien d'autre — or **une épreuve dont on ne relit pas les
 * erreurs n'a rien appris à personne**. Le score dit qu'il y a un problème ; la
 * relecture est le seul endroit où il se répare.
 *
 * **Les questions justes y figurent aussi.** Une bonne réponse trouvée par
 * hasard mérite qu'on lise pourquoi elle était bonne — et c'est justement
 * celle-là qui retombera au concours.
 *
 * **Pliée par défaut.** Le bilan doit d'abord donner le score ; dérouler dix
 * cartes sous le chiffre ferait scroller pour trouver le résultat qu'on est
 * venu chercher. Le candidat ouvre s'il veut travailler.
 *
 * **On réutilise `ItemLine` telle quelle.** Une carte relue est une carte
 * répondue : mêmes couleurs d'options, même bloc de correction, même gris pour
 * le choix raté. En réécrire une version « lecture seule » ferait diverger la
 * plus lue des deux au premier changement de style.
 */
export function RelectureDesReponses({
  cartes,
  devoilements,
  reponses,
  economieDeDonnees = false,
}: {
  cartes: Carte[]
  /** Les corrections du SERVEUR, par `item_id`. */
  devoilements: Record<string, Devoilement>
  /**
   * Ce que le candidat a composé, par `item_id`. Absent = laissé en blanc.
   *
   * §150 — un appariement y porte ses paires : sans elles, une question relue
   * s'afficherait vierge et le candidat croirait sa réponse perdue.
   */
  reponses: Record<string, ReponseComposee>
  economieDeDonnees?: boolean
}) {
  const [ouverte, setOuverte] = useState(false)

  // Sans aucune correction reçue — réseau coupé pendant toute l'épreuve — il
  // n'y a rien à relire, et proposer un bouton qui ouvre du vide est pire que
  // ne rien proposer.
  const relisibles = cartes.filter((c) => devoilements[c.item_id] !== undefined)

  if (relisibles.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => {
          setOuverte((avant) => !avant)
        }}
        aria-expanded={ouverte}
        className="flex items-center justify-between gap-2 rounded-2xl border p-3 text-left"
        style={{ borderColor: 'var(--bordure)' }}
      >
        <span>
          <span className="block text-[15px] font-bold">Revoir mes réponses</span>
          <span className="block text-[13px]" style={{ color: 'var(--texte-2)' }}>
            {relisibles.length} question{relisibles.length > 1 ? 's' : ''}, avec les
            explications
          </span>
        </span>

        <span
          className="shrink-0 transition-transform"
          style={{ transform: ouverte ? 'rotate(180deg)' : undefined }}
        >
          <Icone nom="chevron-bas" taille={18} />
        </span>
      </button>

      {ouverte && (
        <ol className="flex flex-col">
          {relisibles.map((carte, rang) => (
            <li
              key={carte.item_id}
              className="border-t pt-3"
              style={{ borderColor: 'var(--bordure)' }}
            >
              <p className="px-4 text-[13px] font-bold" style={{ color: 'var(--texte-2)' }}>
                Question {rang + 1}
                {reponses[carte.item_id] === undefined && ' — laissée en blanc'}
              </p>

              <ItemLine
                carte={carte}
                // Le dévoilement du serveur : c'est LUI qui porte les clés et
                // l'explication, la carte d'une épreuve n'en portant aucune.
                devoilement={devoilements[carte.item_id]}
                choixInitial={reponses[carte.item_id]}
                economieDeDonnees={economieDeDonnees}
                // Rien à renvoyer : la question est déjà répondue et le
                // journal fermé. `ItemLine` verrouille d'elle-même dès qu'un
                // dévoilement lui arrive.
                onRepondre={() => {
                  /* relecture seule */
                }}
              />
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
